import { useContext, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { MyContext } from '../../../context';
import DataTable from '../../../components/tables/DataTable';
import Pagination from '../../../components/Pagination';
import StatusBadge from '../../../components/StatusBadge';
import ImageWithFallback from '../../../components/ImageWithFallback';
import { listKits } from '../../../utils/queries';
import { useCan } from '../../../hooks/useCan';
import { PLATFORMADMIN_ROLES } from '../../../components/navConfig';

const PAGE_SIZE = 20;

const STATUS_DISPLAY = {
	active: { status: 'active', text: 'Active' },
	inactive: { status: 'inactive', text: 'Inactive' },
	discontinued: { status: 'inactive', text: 'Discontinued' },
};

export default function KitsList() {
	const { activeStoreId } = useContext(MyContext);
	const canCreateKit = useCan(PLATFORMADMIN_ROLES);
	const navigate = useNavigate();
	const [kits, setKits] = useState([]);
	const [total, setTotal] = useState(0);
	const [page, setPage] = useState(0);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState('');

	const refresh = () => {
		setLoading(true);
		listKits({
			limit: PAGE_SIZE,
			offset: page * PAGE_SIZE,
			search: search || undefined,
			storeId: activeStoreId || undefined,
		}).then((result) => {
			if (result?.success) {
				setKits(result.data.kits);
				setTotal(result.data.pagination.total);
			}
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, search, activeStoreId]);

	const columns = [
		{
			key: 'name',
			header: 'Name',
			render: (value, row) => (
				<div className='flex items-center gap-3'>
					<div className='size-10 shrink-0 rounded-md bg-gray-100 overflow-hidden'>
						<ImageWithFallback src={row.imageUrl} alt={value} className='size-full object-cover border border-gray-200' fallbackClassName='size-full' />
					</div>
					<div className='min-w-0'>
						<Link
							to={`/products/kits/${row.productId}/edit`}
							className='block text-black font-medium hover:underline truncate'
						>
							{value}
						</Link>
						<p className='text-xs text-gray-400 truncate'>
							{row.itemCount} product{Number(row.itemCount) === 1 ? '' : 's'}
						</p>
					</div>
				</div>
			),
		},
		{
			key: 'status',
			header: 'Status',
			render: (value) => {
				const display = STATUS_DISPLAY[value] || { status: value, text: value };
				return <StatusBadge status={display.status} text={display.text} />;
			},
		},
	];

	return (
		<div className='py-8 pr-8'>
			<div className='flex items-center justify-between mb-6'>
				<h1 className='text-2xl font-light tracking-tight'>Kits</h1>
				{canCreateKit && (
					<button type='button' className='btn-primary' onClick={() => navigate('/products/kits/new')}>
						New Kit
					</button>
				)}
			</div>

			<div className='mb-5 flex items-end gap-4'>
				<div>
					<label>Search</label>
					<div className='relative max-w-sm'>
						<Search className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 size-4 text-gray-400' />
						<input
							className='w-52 form-input pl-9'
							value={search}
							onChange={(e) => {
								setSearch(e.target.value);
								setPage(0);
							}}
						/>
					</div>
				</div>
			</div>

			<DataTable columns={columns} data={kits} loading={loading} emptyHeading='No kits found' />
			<Pagination pageCount={Math.ceil(total / PAGE_SIZE)} currentPage={page} onPageChange={setPage} />
		</div>
	);
}
